import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useAuth } from "../../../context/AuthContext";
import { useDashboardTheme } from "../../../context/DashboardThemeContext";
import { useAlert } from "../../../context/AlertContext";

const roles = [
  { label: "Administrador", value: "admin" },
  { label: "Staff", value: "staff" },
];

const getUserName = (item) => item?.name || item?.nombre || "";
const getUserEmail = (item) => item?.email || item?.correo || "";
const getUserPhone = (item) => item?.phone || item?.telefono || "";
const getUserRole = (item) => item?.role || item?.rol || "staff";
const getUserActive = (item) => {
  if (typeof item?.is_active === "boolean") return item.is_active;
  if (typeof item?.activo === "boolean") return item.activo;
  return true;
};

export default function EditDeleteUsers({
  open,
  mode,
  user,
  onClose,
  onDone,
  editEndpoint = "/api/auth/users/edit/",
  deleteEndpoint = "/api/auth/users/delete/",
}) {
  const { accessToken, logout } = useAuth();
  const { dashboardTheme } = useDashboardTheme();
  const alert = useAlert();

  const isDelete = useMemo(() => mode === "delete", [mode]);

  const [form, setForm] = useState({
    nombre: "",
    correo: "",
    telefono: "",
    rol: "staff",
    activo: true,
  });

  const [loading, setLoading] = useState(false);

  const notify = (message, severity = "success") => {
    if (typeof alert?.showAlert === "function") {
      alert.showAlert(message, severity);
    }

    if (typeof onDone === "function") {
      onDone(message, severity);
    }
  };

  const getToken = () => accessToken || localStorage.getItem("accessToken") || "";

  const parseJsonSafe = async (res) => {
    try {
      return await res.json();
    } catch {
      return { ok: false, error: "Respuesta inválida del servidor" };
    }
  };

  const authFetch = async (url, options = {}) => {
    const token = getToken();

    const headers = {
      Accept: "application/json",
      ...(options.headers || {}),
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  useEffect(() => {
    if (!open) return;

    const nextRole = getUserRole(user);

    setForm({
      nombre: getUserName(user),
      correo: getUserEmail(user),
      telefono: getUserPhone(user),
      rol: ["admin", "staff"].includes(nextRole) ? nextRole : "staff",
      activo: getUserActive(user),
    });
  }, [open, user]);

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: dashboardTheme.card_background,
      color: dashboardTheme.text_color,
      "& fieldset": {
        borderColor: dashboardTheme.border_color,
      },
      "&:hover fieldset": {
        borderColor: dashboardTheme.primary_color,
      },
      "&.Mui-focused fieldset": {
        borderColor: dashboardTheme.primary_color,
      },
    },
    "& .MuiInputLabel-root": {
      color: dashboardTheme.muted_text_color,
      "&.Mui-focused": {
        color: dashboardTheme.primary_color,
      },
    },
    "& .MuiSvgIcon-root": {
      color: dashboardTheme.muted_text_color,
    },
  };

  const primaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    bgcolor: isDelete ? "#dc2626" : dashboardTheme.button_color,
    color: "#ffffff",
    boxShadow: "none",
    "&:hover": {
      bgcolor: isDelete ? "#b91c1c" : dashboardTheme.button_hover_color || dashboardTheme.button_color,
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    color: dashboardTheme.text_color,
    borderColor: dashboardTheme.border_color,
    "&:hover": {
      borderColor: dashboardTheme.primary_color,
      bgcolor: `${dashboardTheme.primary_color}12`,
    },
  };

  const closeSafe = () => {
    if (loading) return;
    onClose();
  };

  const handleChange = (event) => {
    setForm((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const doEdit = async () => {
    if (!user?.id) return;

    if (!form.nombre.trim() || !form.correo.trim()) {
      notify("Nombre y correo son obligatorios", "error");
      return;
    }

    if (!["admin", "staff"].includes(form.rol)) {
      notify("Rol no permitido.", "error");
      return;
    }

    try {
      setLoading(true);

      const res = await authFetch(editEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: user.id,
          name: form.nombre.trim(),
          email: form.correo.trim().toLowerCase(),
          phone: (form.telefono || "").trim(),
          role: form.rol,
          is_active: !!form.activo,
          nombre: form.nombre.trim(),
          correo: form.correo.trim().toLowerCase(),
          telefono: (form.telefono || "").trim(),
          rol: form.rol,
          activo: !!form.activo,
        }),
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (res.ok && data.ok) {
        onClose();
        notify(data?.message || data?.msg || "Usuario actualizado", "success");
      } else {
        notify(data?.error || "No se pudo actualizar", "error");
      }
    } catch {
      notify("Error de conexión", "error");
    } finally {
      setLoading(false);
    }
  };

  const doDelete = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      const res = await authFetch(deleteEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: user.id }),
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (res.ok && data.ok) {
        onClose();
        notify(data?.message || data?.msg || "Usuario eliminado", "success");
      } else {
        notify(data?.error || "No se pudo eliminar", "error");
      }
    } catch {
      notify("Error de conexión", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={closeSafe}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(480px, calc(100vw - 32px))",
          bgcolor: dashboardTheme.card_background,
          borderRadius: "16px",
          p: { xs: 2, sm: 3 },
          border: `1px solid ${dashboardTheme.border_color}`,
          boxShadow:
            dashboardTheme.theme_mode === "dark"
              ? "0 24px 60px rgba(0,0,0,0.45)"
              : "0 24px 60px rgba(17,24,39,0.18)",
          outline: "none",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            color: isDelete ? "#dc2626" : dashboardTheme.primary_color,
            mb: 1,
            fontSize: 18,
          }}
        >
          {isDelete ? "Eliminar usuario" : "Editar usuario"}
        </Typography>

        <Typography sx={{ color: dashboardTheme.muted_text_color, mb: 2 }}>
          {getUserName(user) ? `Usuario: ${getUserName(user)}` : "Usuario seleccionado"}
        </Typography>

        {isDelete ? (
          <>
            <Typography sx={{ color: dashboardTheme.muted_text_color, mb: 2, lineHeight: 1.6 }}>
              Esta acción eliminará el usuario de forma permanente.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} justifyContent="flex-end">
              <Button
                onClick={closeSafe}
                disabled={loading}
                variant="outlined"
                sx={secondaryButtonSx}
              >
                Cancelar
              </Button>

              <Button
                variant="contained"
                onClick={doDelete}
                disabled={loading}
                sx={primaryButtonSx}
              >
                {loading ? "Eliminando..." : "Eliminar"}
              </Button>
            </Stack>
          </>
        ) : (
          <Stack spacing={2}>
            <TextField
              label="Nombre completo"
              name="nombre"
              size="small"
              fullWidth
              value={form.nombre}
              onChange={handleChange}
              disabled={loading}
              sx={fieldSx}
            />

            <TextField
              label="Correo"
              name="correo"
              size="small"
              fullWidth
              value={form.correo}
              onChange={handleChange}
              disabled={loading}
              sx={fieldSx}
            />

            <TextField
              label="Teléfono"
              name="telefono"
              size="small"
              fullWidth
              value={form.telefono}
              onChange={handleChange}
              disabled={loading}
              sx={fieldSx}
            />

            <FormControl fullWidth size="small" sx={fieldSx}>
              <InputLabel>Rol</InputLabel>
              <Select
                label="Rol"
                name="rol"
                value={form.rol}
                onChange={handleChange}
                disabled={loading}
              >
                {roles.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={!!form.activo}
                  disabled={loading}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      activo: event.target.checked,
                    }))
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: dashboardTheme.primary_color,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: dashboardTheme.primary_color,
                    },
                  }}
                />
              }
              label={form.activo ? "Activo" : "Inactivo"}
              sx={{ color: dashboardTheme.text_color }}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} justifyContent="flex-end">
              <Button
                onClick={closeSafe}
                disabled={loading}
                variant="outlined"
                sx={secondaryButtonSx}
              >
                Cancelar
              </Button>

              <Button
                variant="contained"
                onClick={doEdit}
                disabled={loading}
                sx={primaryButtonSx}
              >
                {loading ? "Guardando..." : "Guardar cambios"}
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
    </Modal>
  );
}